import { useEffect, useState, useMemo, type CSSProperties } from "react";
import {
  getMemberOperatingView,
  getMemberDetail,
  updateMemberStatus,
  type MemberOperatingViewRow,
  type MemberDetailResponse,
} from "../../lib/api";
import GuestStudioTabs from "../../components/admin/GuestStudioTabs";
import { useIsMobile } from "../../lib/useIsMobile";

const VIEW_TITLES: Record<string, string> = {
  all: "All Members",
  needs_outreach: "Needs Outreach",
  renewals_soon: "Renewals Soon",
  high_value_low_visits: "High Value / Low Visits",
  unused_pours: "Unused Pours",
  recent_joiners: "Recent Joiners",
  birthdays: "Birthdays This Month",
  data_issues: "Data Issues",
};

export default function AdminMembers() {
  // Phone (<768): stack side-by-side panes (they overflowed the 343px content width).
  const isPhone = useIsMobile(768);
  const [members, setMembers] = useState<MemberOperatingViewRow[]>([]);
  const [activeView, setActiveView] = useState<string>("all");
  const [quickFilters, setQuickFilters] = useState<Set<string>>(new Set());
  const [riskFilter, setRiskFilter] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<string>("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Selected Member Detail Modal
  const [selectedMember, setSelectedMember] = useState<MemberOperatingViewRow | null>(null);
  const [detailData, setDetailData] = useState<MemberDetailResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editStatus, setEditStatus] = useState<string>("");
  const [editTermEnd, setEditTermEnd] = useState<string>("");
  const [editAutoRenew, setEditAutoRenew] = useState<boolean>(true);
  const [savingEdit, setSavingEdit] = useState(false);
  const [showMemberCardModal, setShowMemberCardModal] = useState(false);

  const loadData = async (viewToLoad = activeView) => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const data = await getMemberOperatingView(viewToLoad);
      setMembers(data || []);
    } catch (err: any) {
      setErrorMsg(err?.message || "Failed to load members");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(activeView);
  }, [activeView]);

  const handleSelectView = (view: string) => {
    setActiveView(view);
    setQuickFilters(new Set());
  };

  const toggleQuickFilter = (filterKey: string) => {
    setQuickFilters((prev) => {
      const next = new Set(prev);
      if (next.has(filterKey)) next.delete(filterKey);
      else next.add(filterKey);
      return next;
    });
  };

  // KPI Calculations
  const activeCount = useMemo(
    () =>
      members.filter(
        (m) =>
          m.lifecycle_status === "active" ||
          m.billing_status === "paid" ||
          m.billing_status === "renewal_soon"
      ).length,
    [members]
  );
  const atRiskCount = useMemo(() => members.filter((m) => m.risk_label === "At Risk").length, [members]);
  const renewalsSoonCount = useMemo(
    () => members.filter((m) => m.days_until_renewal !== null && m.days_until_renewal <= 45).length,
    [members]
  );
  const unusedPoursCount = useMemo(() => members.filter((m) => m.pours_used === 0).length, [members]);

  const topSpenderThreshold = useMemo(() => {
    if (!members.length) return 0;
    const sorted = members.map((m) => m.total_spend_cents || 0).sort((a, b) => b - a);
    return sorted[Math.floor(sorted.length * 0.2)] || 0;
  }, [members]);

  const isBirthdayThisMonth = (dob: string | null) => {
    if (!dob) return false;
    try {
      const d = new Date(dob + "T12:00:00");
      return d.getMonth() === new Date().getMonth();
    } catch {
      return false;
    }
  };

  // Filtered members
  const filteredMembers = useMemo(() => {
    return members.filter((m) => {
      // Risk filter
      if (riskFilter && m.risk_label !== riskFilter) return false;
      // Type filter
      if (typeFilter && m.membership_type !== typeFilter) return false;

      // Quick Filters
      if (quickFilters.has("no-visit-60") && m.days_since_visit < 60) return false;
      if (
        quickFilters.has("renewal-45") &&
        (m.days_until_renewal === null || m.days_until_renewal > 45)
      )
        return false;
      if (quickFilters.has("unused-pours") && m.pours_used > 0) return false;
      if (quickFilters.has("top-spenders") && (m.total_spend_cents || 0) < topSpenderThreshold)
        return false;
      if (quickFilters.has("birthday") && !isBirthdayThisMonth(m.date_of_birth)) return false;

      // Search Query
      if (search.trim()) {
        const q = search.toLowerCase();
        const nameMatch = (m.name || "").toLowerCase().includes(q);
        const emailMatch = (m.email || "").toLowerCase().includes(q);
        const phoneMatch = (m.phone || "").toLowerCase().includes(q);
        const numMatch = m.member_number ? String(m.member_number).includes(q) : false;
        if (!nameMatch && !emailMatch && !phoneMatch && !numMatch) return false;
      }

      return true;
    });
  }, [members, riskFilter, typeFilter, quickFilters, search, topSpenderThreshold]);

  // Open detail panel
  const handleOpenDetail = async (m: MemberOperatingViewRow) => {
    setSelectedMember(m);
    setEditStatus(m.lifecycle_status || "active");
    setEditTermEnd(m.term_end_date || "");
    setEditAutoRenew(m.auto_renew ?? true);
    setDetailLoading(true);
    try {
      const res = await getMemberDetail(m.customer_id, m.member_id);
      setDetailData(res);
    } catch {
      setDetailData(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSaveMembership = async () => {
    if (!selectedMember) return;
    setSavingEdit(true);
    try {
      const res = await updateMemberStatus(selectedMember.member_id, {
        status: editStatus,
        term_end_date: editTermEnd || undefined,
        auto_renew: editAutoRenew,
      });
      if (res.ok) {
        await loadData(activeView);
        setSelectedMember(null);
      } else {
        alert("Update failed: " + (res.error || "Unknown error"));
      }
    } catch (err: any) {
      alert("Error saving: " + (err?.message || "Network error"));
    } finally {
      setSavingEdit(false);
    }
  };

  const fmtCents = (c: number) => "$" + (Math.round((c || 0) / 100)).toLocaleString();
  const fmtDate = (d: string | null) => {
    if (!d) return "—";
    try {
      return new Date(d.split("T")[0] + "T12:00:00").toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return d;
    }
  };

  return (
    <div style={styles.container}>
      <GuestStudioTabs activeTab="members" badgeCounts={{ members: members.length }} />
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Collective 33 Members</h1>
          <p style={styles.subtitle}>
            Member Operating View, risk monitoring, pour utilization, and renewals
          </p>
        </div>
        <button style={styles.refreshBtn} onClick={() => loadData(activeView)} disabled={loading}>
          {loading ? "Refreshing..." : "↻ Refresh"}
        </button>
      </div>

      {errorMsg && <div style={styles.errorBanner}>{errorMsg}</div>}

      {/* KPI Cards */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statVal}>{activeCount}</div>
          <div style={styles.statLabel}>Active Members</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statVal, color: "var(--red, #E05040)" }}>{atRiskCount}</div>
          <div style={styles.statLabel}>At Risk</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statVal, color: "var(--gold, #F9AF36)" }}>{renewalsSoonCount}</div>
          <div style={styles.statLabel}>Renewals Soon (45d)</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statVal}>{unusedPoursCount}</div>
          <div style={styles.statLabel}>Unused Pours</div>
        </div>
      </div>

      {/* Search & Quick Filter Pills */}
      <div style={styles.searchRow}>
        <input
          type="text"
          style={styles.search}
          placeholder="Search by name, email, phone, member #..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div style={styles.pills}>
          {[
            { key: "no-visit-60", label: "No visit 60d" },
            { key: "renewal-45", label: "Renewal 45d" },
            { key: "unused-pours", label: "Unused pours" },
            { key: "top-spenders", label: "Top spenders" },
            { key: "birthday", label: "Birthday this month" },
          ].map((p) => {
            const active = quickFilters.has(p.key);
            return (
              <button
                key={p.key}
                style={{
                  ...styles.pill,
                  ...(active ? styles.pillActive : {}),
                }}
                onClick={() => toggleQuickFilter(p.key)}
              >
                {p.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Operating View Layout (Sidebar + Content) */}
      <div style={isPhone ? { ...styles.mainLayout, gridTemplateColumns: "minmax(0, 1fr)" } : styles.mainLayout}>
        {/* Sidebar Views. Phones (<768, Phase 4b) get the same three choices as compact dropdowns:
            the 8 view buttons plus filters pushed the members a full screen down. 768+ renders
            master's sidebar unchanged. */}
        {isPhone ? (
          <div style={phoneToolbar}>
            <label style={phoneField}>
              <span style={styles.filterLabel}>View</span>
              <select style={styles.select} value={activeView} onChange={(e) => handleSelectView(e.target.value)}>
                {Object.entries(VIEW_TITLES).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </label>
            <label style={phoneField}>
              <span style={styles.filterLabel}>Risk Level</span>
              <select style={styles.select} value={riskFilter} onChange={(e) => setRiskFilter(e.target.value)}>
                <option value="">All Risk Levels</option>
                <option value="At Risk">At Risk</option>
                <option value="Watch">Watch</option>
                <option value="Healthy">Healthy</option>
              </select>
            </label>
            <label style={phoneField}>
              <span style={styles.filterLabel}>Membership Type</span>
              <select style={styles.select} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                <option value="">All Types</option>
                <option value="standard">Standard</option>
                <option value="gift">Gift</option>
                <option value="ambassador">Ambassador</option>
              </select>
            </label>
          </div>
        ) : (
        <aside style={styles.sidebar}>
          <div style={styles.sectionTitle}>Views</div>
          {Object.entries(VIEW_TITLES).map(([key, label]) => (
            <button
              key={key}
              style={{
                ...styles.viewBtn,
                ...(activeView === key ? styles.viewBtnActive : {}),
              }}
              onClick={() => handleSelectView(key)}
            >
              {label}
            </button>
          ))}

          <div style={{ ...styles.sectionTitle, marginTop: 20 }}>Filters</div>
          <label style={styles.filterLabel}>Risk Level</label>
          <select
            style={styles.select}
            value={riskFilter}
            onChange={(e) => setRiskFilter(e.target.value)}
          >
            <option value="">All Risk Levels</option>
            <option value="At Risk">At Risk</option>
            <option value="Watch">Watch</option>
            <option value="Healthy">Healthy</option>
          </select>

          <label style={{ ...styles.filterLabel, marginTop: 10 }}>Membership Type</label>
          <select
            style={styles.select}
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="">All Types</option>
            <option value="standard">Standard</option>
            <option value="gift">Gift</option>
            <option value="ambassador">Ambassador</option>
          </select>
        </aside>
        )}

        {/* Member Cards List */}
        <div style={styles.content}>
          <div style={styles.contentHeader}>
            <span style={styles.contentTitle}>{VIEW_TITLES[activeView] || "Members"}</span>
            <span style={styles.countBadge}>
              {filteredMembers.length} member{filteredMembers.length !== 1 ? "s" : ""}
            </span>
          </div>

          {loading ? (
            <div style={styles.empty}>Loading members...</div>
          ) : filteredMembers.length === 0 ? (
            <div style={styles.empty}>No members match this view and filters.</div>
          ) : (
            <div style={styles.cardsGrid}>
              {filteredMembers.map((m) => {
                const initials = (m.name || "")
                  .split(" ")
                  .map((w) => w[0])
                  .join("")
                  .slice(0, 2)
                  .toUpperCase();

                const isAtRisk = m.risk_label === "At Risk";
                const isWatch = m.risk_label === "Watch";

                return (
                  <div
                    key={m.member_id}
                    style={isPhone ? { ...styles.card, ...phoneCard } : styles.card}
                    onClick={() => handleOpenDetail(m)}
                  >
                    <div style={styles.avatar}>{initials}</div>
                    <div style={styles.cardBody}>
                      <div style={styles.cardTop}>
                        <span style={styles.cardName}>{m.name || "Unnamed Member"}</span>
                        {m.member_number && (
                          <span style={styles.memberNum}>#{m.member_number}</span>
                        )}
                      </div>
                      <div style={styles.cardDates}>
                        Joined {fmtDate(m.joined_at)}
                        {m.term_end_date && ` · Renews ${fmtDate(m.term_end_date)}`}
                      </div>
                      <div style={styles.metricsRow}>
                        <span><strong>{m.visit_count || 0}</strong> visits</span>
                        <span><strong>{fmtCents(m.total_spend_cents)}</strong> spent</span>
                        <span><strong>{m.pours_used}/{m.pours_allowed}</strong> pours</span>
                        {m.days_since_visit < 999 && (
                          <span>Last visit <strong>{m.days_since_visit}d</strong> ago</span>
                        )}
                      </div>
                    </div>

                    <div style={isPhone ? { ...styles.cardRight, ...phoneCardRight } : styles.cardRight}>
                      <span
                        style={{
                          ...styles.riskBadge,
                          ...(isAtRisk
                            ? styles.riskAtRisk
                            : isWatch
                            ? styles.riskWatch
                            : styles.riskHealthy),
                        }}
                      >
                        {m.risk_label}
                      </span>
                      <span style={styles.billingBadge}>
                        {m.billing_status?.replace("_", " ") || "paid"}
                      </span>
                      <span style={styles.actionText}>{m.next_best_action}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Member Detail Slide-over / Modal */}
      {selectedMember && (
        <div style={styles.modalOverlay} onClick={() => setSelectedMember(null)}>
          <div style={styles.modalBody} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <h2 style={styles.modalTitle}>{selectedMember.name}</h2>
                <p style={styles.modalMeta}>
                  Member #{selectedMember.member_number || "—"} · {selectedMember.membership_type}
                </p>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <button
                  type="button"
                  style={styles.cardBadgeBtn}
                  onClick={() => setShowMemberCardModal(true)}
                >
                  📇 Issue C33 VIP Pass
                </button>
                <button style={styles.closeBtn} onClick={() => setSelectedMember(null)}>
                  ✕
                </button>
              </div>
            </div>

            {/* Quick Contact Bar */}
            <div style={styles.contactBar}>
              <div>📧 {selectedMember.email || "No email"}</div>
              <div>📞 {selectedMember.phone || "No phone"}</div>
              <div>🎂 {selectedMember.date_of_birth || "No birthday"}</div>
            </div>

            {/* Edit / Manage Section */}
            <div style={styles.detailSection}>
              <div style={styles.sectionHeading}>Membership Configuration</div>
              <div style={isPhone ? { ...styles.editGrid, gridTemplateColumns: "minmax(0, 1fr)" } : styles.editGrid}>
                <div>
                  <label style={styles.fieldLabel}>Status</label>
                  <select
                    style={styles.select}
                    value={editStatus}
                    onChange={(e) => setEditStatus(e.target.value)}
                  >
                    <option value="active">Active</option>
                    <option value="lapsed">Lapsed</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
                <div>
                  <label style={styles.fieldLabel}>Term End Date</label>
                  <input
                    type="date"
                    style={styles.input}
                    value={editTermEnd}
                    onChange={(e) => setEditTermEnd(e.target.value)}
                  />
                </div>
                <div>
                  <label style={styles.fieldLabel}>Auto Renew</label>
                  <div style={{ marginTop: 8 }}>
                    <label style={{ cursor: "pointer", color: "var(--text)" }}>
                      <input
                        type="checkbox"
                        checked={editAutoRenew}
                        onChange={(e) => setEditAutoRenew(e.target.checked)}
                      />
                      &nbsp; Auto-Renew Enabled
                    </label>
                  </div>
                </div>
              </div>
              <button
                style={styles.saveBtn}
                disabled={savingEdit}
                onClick={handleSaveMembership}
              >
                {savingEdit ? "Saving..." : "Save Membership Changes"}
              </button>
            </div>

            {/* Redemptions & Purchases breakdown */}
            {detailLoading ? (
              <div style={{ textAlign: "center", padding: 24, color: "var(--muted)" }}>
                Loading member history...
              </div>
            ) : detailData ? (
              <>
                {/* Orders / Visits summary */}
                <div style={styles.detailSection}>
                  <div style={styles.sectionHeading}>
                    Order History ({detailData.orders?.length || 0} visits)
                  </div>
                  <div>
                    {(detailData.orders || []).length === 0 ? (
                      <p style={{ color: "var(--muted)", fontSize: 12 }}>No order history recorded.</p>
                    ) : (
                      (detailData.orders || []).slice(0, 6).map((o: any, idx: number) => (
                        <div key={idx} style={styles.historyRow}>
                          <span>{fmtDate(o.closed_at)}</span>
                          <span>{o.item_summary || "Ticket"}</span>
                          <strong>{fmtCents(o.total_money_cents)}</strong>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Pour Redemptions */}
                <div style={styles.detailSection}>
                  <div style={styles.sectionHeading}>
                    Pour Benefit Redemptions ({detailData.redemptions?.length || 0})
                  </div>
                  <div>
                    {(detailData.redemptions || []).length === 0 ? (
                      <p style={{ color: "var(--muted)", fontSize: 12 }}>No pour redemptions yet.</p>
                    ) : (
                      (detailData.redemptions || []).slice(0, 6).map((r: any, idx: number) => (
                        <div key={idx} style={styles.historyRow}>
                          <span>{fmtDate(r.redeemed_at)}</span>
                          <span>{r.item_name || "Monthly Pour"}</span>
                          <span style={{ color: "var(--green, #27ae60)" }}>✓ Redeemed</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}

      {/* ═══ PRINTABLE C33 VIP SOCIETY PASS MODAL ═══ */}
      {showMemberCardModal && selectedMember && (
        <div style={styles.modalOverlay} onClick={() => setShowMemberCardModal(false)}>
          <div style={{ ...styles.modalBody, maxWidth: 540 }} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <h2 style={styles.modalTitle}>C33 Society VIP Credential</h2>
                <p style={styles.modalMeta}>Official digital & printable membership pass</p>
              </div>
              <button style={styles.closeBtn} onClick={() => setShowMemberCardModal(false)}>✕</button>
            </div>

            {/* Printable Physical Member Pass */}
            <div className="printable-member-pass" style={styles.memberPassFrame}>
              <div style={styles.passInnerBorder}>
                <div style={styles.passHeader}>
                  <div style={styles.passLogo}>THE SENTINEL ROOM</div>
                  <div style={styles.passSociety}>CENTURY 33 SOCIETY VIP</div>
                </div>

                <div style={styles.passBody}>
                  <div style={styles.passName}>{selectedMember.name}</div>
                  <div style={styles.passNumber}>
                    MEMBER #{String(selectedMember.member_number || "0042").padStart(4, "0")}
                  </div>
                  
                  <div style={styles.passBadgeRow}>
                    <span style={styles.passTierBadge}>
                      {(selectedMember.membership_type || "basic").toUpperCase()} PATRON
                    </span>
                    <span style={styles.passStatusBadge}>
                      STATUS: {(selectedMember.lifecycle_status || "active").toUpperCase()}
                    </span>
                  </div>
                </div>

                {/* VIP Perks */}
                <div style={styles.passPerksGrid}>
                  <div style={styles.passPerkItem}>🥃 12 Rare Pours / Year</div>
                  <div style={styles.passPerkItem}>🔐 Spirit Locker Privileges</div>
                  <div style={styles.passPerkItem}>🚪 Priority Speakeasy Entry</div>
                  <div style={styles.passPerkItem}>✨ Master Distiller Classes</div>
                </div>

                <div style={styles.passFooter}>
                  <div>
                    <span style={styles.passSmallLabel}>MEMBER ID</span>
                    <span style={styles.passSmallVal}>{selectedMember.member_id.slice(0, 8).toUpperCase()}</span>
                  </div>
                  <div>
                    <span style={styles.passSmallLabel}>STATUS</span>
                    <span style={styles.passSmallVal}>{(selectedMember.lifecycle_status || "active").toUpperCase()}</span>
                  </div>
                  <div>
                    <span style={styles.passSmallLabel}>TERM END</span>
                    <span style={styles.passSmallVal}>{fmtDate(selectedMember.term_end_date || null)}</span>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button style={styles.cancelBtn} onClick={() => setShowMemberCardModal(false)}>Close</button>
              <button style={styles.saveBtn} onClick={() => window.print()}>🖨️ Print Member Pass</button>
            </div>
          </div>
        </div>
      )}

      {/* Member Pass Print Stylesheet */}
      <style>{`
        @media print {
          @page {
            size: 3.5in 2.25in landscape;
            margin: 0.15in;
          }
          body * {
            visibility: hidden !important;
          }
          .printable-member-pass, .printable-member-pass * {
            visibility: visible !important;
          }
          .printable-member-pass {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            background: #110e0a !important;
            color: #F0E6D4 !important;
            border: 2px solid #F9AF36 !important;
            padding: 10px !important;
            box-shadow: none !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
      `}</style>
    </div>
  );
}

// Phones only (<768, Phase 4b). The right-hand badge column never shrank, so a long next action
// squeezed the member's details into a column a few words wide. On phones it becomes its own row
// under the details.
const phoneToolbar: CSSProperties = { display: "flex", flexWrap: "wrap", gap: 10 };
const phoneField: CSSProperties = { flex: "1 1 140px", minWidth: 0 };
const phoneCard: CSSProperties = { flexWrap: "wrap", alignItems: "flex-start" };
const phoneCardRight: CSSProperties = { flexBasis: "100%", flexDirection: "row", flexWrap: "wrap", alignItems: "center", justifyContent: "flex-start", gap: 8, flexShrink: 1 };

const styles: Record<string, CSSProperties> = {
  container: {
    maxWidth: 1100,
    margin: "0 auto",
    padding: "24px 20px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
    flexWrap: "wrap",
    gap: 12,
  },
  title: {
    fontFamily: "Georgia, serif",
    fontSize: 24,
    color: "var(--gold, #F9AF36)",
    margin: "0 0 4px 0",
  },
  subtitle: {
    fontSize: 13,
    color: "var(--muted, #8A7A66)",
    margin: 0,
  },
  refreshBtn: {
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    color: "var(--text, #F0E6D4)",
    borderRadius: 6,
    padding: "8px 14px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  errorBanner: {
    background: "rgba(224, 80, 64, 0.15)",
    border: "1px solid rgba(224, 80, 64, 0.4)",
    color: "#ff8b7b",
    padding: "10px 14px",
    borderRadius: 6,
    marginBottom: 16,
    fontSize: 13,
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 12,
    marginBottom: 20,
  },
  statCard: {
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 8,
    padding: "16px 12px",
    textAlign: "center",
  },
  statVal: {
    fontFamily: "Georgia, serif",
    fontSize: 26,
    fontWeight: 700,
    color: "var(--gold, #F9AF36)",
  },
  statLabel: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: "var(--muted, #8A7A66)",
    marginTop: 4,
  },
  searchRow: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    marginBottom: 20,
  },
  search: {
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 6,
    padding: "10px 14px",
    fontSize: 14,
    color: "var(--text, #F0E6D4)",
  },
  pills: {
    display: "flex",
    gap: 6,
    flexWrap: "wrap",
  },
  pill: {
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    color: "var(--muted, #8A7A66)",
    borderRadius: 20,
    padding: "5px 12px",
    fontSize: 12,
    cursor: "pointer",
  },
  pillActive: {
    background: "var(--gold, #F9AF36)",
    color: "#111",
    borderColor: "var(--gold, #F9AF36)",
    fontWeight: 600,
  },
  mainLayout: {
    display: "grid",
    gridTemplateColumns: "220px 1fr",
    gap: 20,
  },
  sidebar: {
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 8,
    padding: 16,
    height: "fit-content",
  },
  sectionTitle: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: "var(--muted, #8A7A66)",
    marginBottom: 10,
    fontWeight: 700,
  },
  viewBtn: {
    display: "block",
    width: "100%",
    textAlign: "left",
    background: "transparent",
    border: "none",
    borderRadius: 5,
    padding: "8px 10px",
    fontSize: 13,
    color: "var(--muted, #8A7A66)",
    cursor: "pointer",
    marginBottom: 4,
  },
  viewBtnActive: {
    background: "rgba(249, 175, 54, 0.12)",
    color: "var(--gold, #F9AF36)",
    fontWeight: 600,
  },
  filterLabel: {
    display: "block",
    fontSize: 11,
    color: "var(--muted, #8A7A66)",
    marginBottom: 4,
  },
  select: {
    width: "100%",
    background: "var(--bg, #0D0B08)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 5,
    padding: "7px 10px",
    fontSize: 12,
    color: "var(--text, #F0E6D4)",
  },
  input: {
    width: "100%",
    background: "var(--bg, #0D0B08)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 5,
    padding: "7px 10px",
    fontSize: 12,
    color: "var(--text, #F0E6D4)",
    boxSizing: "border-box",
  },
  content: {},
  contentHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  contentTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: "var(--text, #F0E6D4)",
  },
  countBadge: {
    fontSize: 12,
    color: "var(--muted, #8A7A66)",
  },
  cardsGrid: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  card: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 8,
    padding: "14px 16px",
    cursor: "pointer",
    transition: "border-color 0.2s",
  },
  avatar: {
    width: 42,
    height: 42,
    borderRadius: "50%",
    background: "rgba(249, 175, 54, 0.15)",
    color: "var(--gold, #F9AF36)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    fontWeight: 700,
    flexShrink: 0,
  },
  cardBody: {
    flex: 1,
    minWidth: 0,
  },
  cardTop: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 2,
  },
  cardName: {
    fontSize: 15,
    fontWeight: 700,
    color: "var(--text, #F0E6D4)",
  },
  memberNum: {
    fontSize: 12,
    fontFamily: "monospace",
    color: "var(--gold, #F9AF36)",
  },
  cardDates: {
    fontSize: 12,
    color: "var(--muted, #8A7A66)",
    marginBottom: 6,
  },
  metricsRow: {
    display: "flex",
    gap: 12,
    fontSize: 12,
    color: "var(--text, #F0E6D4)",
    flexWrap: "wrap",
  },
  cardRight: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: 4,
    flexShrink: 0,
  },
  riskBadge: {
    padding: "2px 8px",
    borderRadius: 4,
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
  },
  riskHealthy: {
    background: "rgba(39, 174, 96, 0.15)",
    color: "#4ade80",
  },
  riskWatch: {
    background: "rgba(249, 175, 54, 0.15)",
    color: "var(--gold, #F9AF36)",
  },
  riskAtRisk: {
    background: "rgba(224, 80, 64, 0.15)",
    color: "#ff7666",
  },
  billingBadge: {
    fontSize: 11,
    color: "var(--muted, #8A7A66)",
    textTransform: "capitalize",
  },
  actionText: {
    fontSize: 11,
    color: "var(--muted, #8A7A66)",
  },
  empty: {
    textAlign: "center",
    padding: 40,
    color: "var(--muted, #8A7A66)",
    background: "var(--surface, #13100B)",
    borderRadius: 8,
    border: "1px solid var(--border, #2A1F14)",
  },

  // Modal
  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.75)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: 20,
  },
  modalBody: {
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 10,
    width: 600,
    maxWidth: "95vw",
    maxHeight: "85vh",
    overflowY: "auto",
    padding: 24,
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 16,
    borderBottom: "1px solid var(--border, #2A1F14)",
    paddingBottom: 12,
  },
  modalTitle: {
    fontFamily: "Georgia, serif",
    fontSize: 20,
    color: "var(--gold, #F9AF36)",
    margin: "0 0 4px 0",
  },
  modalMeta: {
    fontSize: 12,
    color: "var(--muted, #8A7A66)",
    margin: 0,
  },
  closeBtn: {
    background: "none",
    border: "none",
    fontSize: 18,
    color: "var(--muted, #8A7A66)",
    cursor: "pointer",
  },
  contactBar: {
    display: "flex",
    gap: 16,
    fontSize: 12,
    color: "var(--text, #F0E6D4)",
    background: "rgba(0,0,0,0.2)",
    padding: "8px 12px",
    borderRadius: 6,
    marginBottom: 16,
    flexWrap: "wrap",
  },
  detailSection: {
    marginBottom: 16,
    background: "rgba(0,0,0,0.15)",
    padding: 14,
    borderRadius: 6,
    border: "1px solid var(--border, #2A1F14)",
  },
  sectionHeading: {
    fontSize: 13,
    fontWeight: 700,
    color: "var(--gold, #F9AF36)",
    marginBottom: 10,
  },
  editGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 10,
    marginBottom: 12,
  },
  fieldLabel: {
    display: "block",
    fontSize: 11,
    color: "var(--muted, #8A7A66)",
    marginBottom: 4,
  },
  saveBtn: {
    background: "var(--gold, #F9AF36)",
    color: "#111",
    border: "none",
    borderRadius: 5,
    padding: "7px 14px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  historyRow: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 12,
    padding: "6px 0",
    borderBottom: "1px solid rgba(255,255,255,0.04)",
  },
  cardBadgeBtn: {
    background: "rgba(249, 175, 54, 0.15)",
    border: "1px solid var(--gold, #F9AF36)",
    color: "var(--gold, #F9AF36)",
    padding: "5px 10px",
    borderRadius: 5,
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  cancelBtn: {
    background: "transparent",
    color: "var(--muted, #8A7A66)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 5,
    padding: "7px 14px",
    fontSize: 12,
    cursor: "pointer",
  },
  memberPassFrame: {
    background: "linear-gradient(135deg, #18140e 0%, #0d0b08 100%)",
    border: "2px solid #F9AF36",
    borderRadius: 12,
    padding: 8,
    boxShadow: "0 10px 30px rgba(0,0,0,0.6)",
  },
  passInnerBorder: {
    border: "1px solid rgba(249, 175, 54, 0.4)",
    borderRadius: 8,
    padding: 16,
  },
  passHeader: {
    textAlign: "center",
    borderBottom: "1px solid rgba(249, 175, 54, 0.3)",
    paddingBottom: 8,
    marginBottom: 12,
  },
  passLogo: {
    fontFamily: "Georgia, serif",
    fontSize: 15,
    fontWeight: 800,
    letterSpacing: 2,
    color: "#F9AF36",
  },
  passSociety: {
    fontSize: 10,
    letterSpacing: 1.5,
    color: "#e0d0b8",
    marginTop: 2,
  },
  passBody: {
    textAlign: "center",
    marginBottom: 14,
  },
  passName: {
    fontFamily: "Georgia, serif",
    fontSize: 22,
    fontWeight: 700,
    color: "#fff",
    marginBottom: 4,
  },
  passNumber: {
    fontSize: 12,
    fontFamily: "monospace",
    letterSpacing: 2,
    color: "#F9AF36",
    marginBottom: 10,
  },
  passBadgeRow: {
    display: "flex",
    justifyContent: "center",
    gap: 8,
  },
  passTierBadge: {
    background: "rgba(249, 175, 54, 0.2)",
    border: "1px solid #F9AF36",
    color: "#F9AF36",
    fontSize: 10,
    fontWeight: 800,
    padding: "3px 8px",
    borderRadius: 4,
    letterSpacing: 0.5,
  },
  passStatusBadge: {
    background: "rgba(39, 174, 96, 0.2)",
    border: "1px solid #27ae60",
    color: "#4ade80",
    fontSize: 10,
    fontWeight: 800,
    padding: "3px 8px",
    borderRadius: 4,
    letterSpacing: 0.5,
  },
  passPerksGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 6,
    background: "rgba(0,0,0,0.3)",
    border: "1px solid rgba(255,255,255,0.05)",
    padding: "10px 12px",
    borderRadius: 6,
    marginBottom: 14,
  },
  passPerkItem: {
    fontSize: 10,
    color: "#d5c5b0",
  },
  passFooter: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    textAlign: "center",
    borderTop: "1px solid rgba(249, 175, 54, 0.3)",
    paddingTop: 10,
  },
  passSmallLabel: {
    display: "block",
    fontSize: 8,
    letterSpacing: 0.5,
    color: "#8A7A66",
    marginBottom: 2,
  },
  passSmallVal: {
    fontSize: 10,
    fontWeight: 700,
    color: "#F9AF36",
  },
};
